import { setLoadingStateForFolderTree } from '../actions/setLoadingStateForFolderTree';
import { getArchiveFolders } from '../actions/publicActions';
import getFolderTreeLoadingState from '../selectors/getFolderTreeLoadingState';
import getFolderTreeCurrentLoadedIndex from '../selectors/getFolderTreeCurrentLoadedIndex';
import FolderTreeLoadStateEnum from '../store/schema/FolderTreeLoadStateEnum';
import addArchiveMappingsInSessionStore from '../util/addArchiveMappingsInSessionStore';
import getDefaultAnchorMailboxForHeaders from '../util/getDefaultAnchorMailboxForHeaders';
import { logUsage } from 'owa-analytics';
import { orchestrator } from 'satcheljs';
import { ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID } from 'owa-folders-constants';
import { fetchArchiveDumpsterFolder } from '../actions/internalActions';
import { getUserConfiguration } from 'owa-session-store';
import { getMailboxInfoForFolderId } from '../util/getMailboxInfoForFolderId';
import { lazyFolderHierarchyDocument } from 'owa-folders-hierarchy-graphql-documents';
import { getApolloClient } from 'owa-apollo';
import { onFolderHierarchyResponse } from '../actions/onFolderHierarchyResponse';

/**
 * An orchestrator that gets the archive folders.
 *
 */
export default orchestrator(getArchiveFolders, async actionMessage => {
    // if loading is in progress or it is loaded and we are not loading more, just return
    const loadingState = getFolderTreeLoadingState(ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID);
    if (
        loadingState === FolderTreeLoadStateEnum.Loading ||
        (loadingState === FolderTreeLoadStateEnum.Loaded && !actionMessage.isLoadingMore)
    ) {
        return;
    }

    let findFolderResponse;

    // sets the loading state to 'Loading' for archive folder tree
    setLoadingStateForFolderTree(
        ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
        FolderTreeLoadStateEnum.Loading
    );
    const loadingIndex = getFolderTreeCurrentLoadedIndex(ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID);
    const mailboxInfo = getMailboxInfoForFolderId(
        ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
        false /*routeToAuxIfAuxArchive*/
    );
    const [apolloClient, query] = await Promise.all([
        getApolloClient(),
        lazyFolderHierarchyDocument.import(),
    ]);

    try {
        const folderHierarchyResponse = await apolloClient.query({
            query,
            variables: {
                baseFolderDistinguishedIds: [ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID],
                offset: loadingIndex,
                mailboxInfo: {
                    userIdentity: mailboxInfo.userIdentity,
                    mailboxSmtpAddress: mailboxInfo.mailboxSmtpAddress,
                    type: mailboxInfo.type,
                },
            },
        });
        findFolderResponse = folderHierarchyResponse.data.folderHierarchy;
    } catch (e) {
        setLoadingStateForFolderTree(
            ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
            FolderTreeLoadStateEnum.ErrorLoading
        );
        return;
    }

    onFolderHierarchyResponse(
        findFolderResponse.RootFolder,
        findFolderResponse.Folders,
        ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
        getDefaultAnchorMailboxForHeaders(), // TODO: This should change to mailboxSmtpAddress for archive mailbox
        getUserConfiguration().SessionSettings.ArchiveDisplayName, // displayName
        ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID, // folderTreeIdentifier
        null, // userIdentity, null value will default it to the correct value for explicit logon scenarios
        findFolderResponse.IncludesLastItemInRange,
        findFolderResponse.offset
    );

    // We are adding the archive root folder and its distinguished children in session store maps
    // This is to easily access folders like deleted items using folderNameToId like we do for primary root.
    addArchiveMappingsInSessionStore(findFolderResponse.RootFolder, findFolderResponse.Folders);

    // fetchArchiveDumpsterFolder only on the first request
    if (loadingIndex === 0) {
        fetchArchiveDumpsterFolder();
        logArchiveMailboxInitialLoad(actionMessage.source);
    }
});

/**
 * Log the Archive Mailbox initial load.
 * @param source - Source of invocation. It can be Folder Pane or Move To
 */
function logArchiveMailboxInitialLoad(source: string) {
    logUsage('Triage_LoadArchiveMailboxFolders', [source]);
}
